import React, { useState } from "react";
import { Database, Edit2, Save, X, Trash2, Eye } from "lucide-react";
import Drawer from "../common/Drawer";

/**
 * Reusable Dynamic Data Editor Component
 * Shows overview cards and opens Drawer for full details
 */
export default function DynamicDataEditor({ data = {}, onChange, title = "Additional Brand Data", excludeKeys = [] }) {
  const [selectedKey, setSelectedKey] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editingKey, setEditingKey] = useState(null);
  const [editForm, setEditForm] = useState({ key: "", value: "" });

  const filteredData = Object.entries(data).filter(([key]) => !excludeKeys.includes(key));

  const handleViewDetails = (key, value) => {
    setSelectedKey(key);
    setEditForm({
      key,
      value: formatValue(value),
      type: detectType(value),
    });
    setIsDrawerOpen(true);
  };

  const detectType = (value) => {
    if (Array.isArray(value)) return "array";
    if (typeof value === "object" && value !== null) return "object";
    if (typeof value === "number") return "number";
    if (typeof value === "boolean") return "boolean";
    return "string";
  };

  const formatValue = (value) => {
    const type = detectType(value);
    if (type === "array" || type === "object") {
      return JSON.stringify(value, null, 2);
    }
    return String(value);
  };

  const parseValue = (valueStr, type) => {
    try {
      if (type === "array" || type === "object") {
        return JSON.parse(valueStr);
      }
      if (type === "number") {
        return Number(valueStr);
      }
      if (type === "boolean") {
        return valueStr === "true" || valueStr === true;
      }
      return valueStr;
    } catch (e) {
      return valueStr;
    }
  };

  const handleSave = () => {
    const parsedValue = parseValue(editForm.value, editForm.type);
    const updated = { ...data };
    
    // Keep the original key, only update the value
    updated[editingKey] = parsedValue;
    onChange(updated);
    setEditingKey(null);
    setEditForm({ key: "", value: "" });
    setIsDrawerOpen(false);
    setSelectedKey(null);
  };

  const handleCancel = () => {
    setEditingKey(null);
    setEditForm({ key: "", value: "" });
    setIsDrawerOpen(false);
    setSelectedKey(null);
  };

  const handleRemove = (key) => {
    const updated = { ...data };
    delete updated[key];
    onChange(updated);
    setIsDrawerOpen(false);
    setSelectedKey(null);
  };


  const getOverviewPreview = (value) => {
    const type = detectType(value);
    
    if (type === "array") {
      return `Array (${value.length} items)`;
    }
    
    if (type === "object") {
      const keys = Object.keys(value);
      return `Object (${keys.length} keys)`;
    }
    
    // For strings, show truncated preview
    const strValue = String(value);
    if (strValue.length > 150) {
      return strValue.substring(0, 150) + "...";
    }
    return strValue;
  };

  const renderFullValue = (value, key) => {
    const type = detectType(value);
    
    // Special handling for products array
    if (type === "array" && key === "products" && value.length > 0 && typeof value[0] === "object" && value[0].name) {
      return (
        <div className="flex flex-col h-full space-y-2">
          <div className="text-sm font-medium" style={{ color: '#0A0A10' }}>Products ({value.length} items)</div>
          <div className="flex-1 space-y-3 overflow-y-auto min-h-0">
            {value.map((product, index) => (
              <div key={index} className="p-4 rounded-lg border" style={{ borderColor: '#D7B472', backgroundColor: '#F8F7F4' }}>
                <div className="font-semibold text-base mb-2" style={{ color: '#0A0A10' }}>
                  {product.name || `Product ${index + 1}`}
                </div>
                {product.description && (
                  <div className="text-sm" style={{ color: '#0A0A10', opacity: 0.8 }}>
                    {product.description}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      );
    }
    
    if (type === "array") {
      return (
        <div className="flex flex-col h-full space-y-2">
          <div className="text-sm font-medium" style={{ color: '#0A0A10' }}>Array ({value.length} items)</div>
          <div className="flex-1 space-y-2 overflow-y-auto min-h-0">
            {value.map((item, index) => (
              <div key={index} className="p-3 rounded-lg border" style={{ borderColor: '#D7B472', backgroundColor: '#F8F7F4' }}>
                <div className="text-xs font-mono mb-1" style={{ color: '#0A0A10', opacity: 0.7 }}>Item {index + 1}</div>
                <pre className="text-sm whitespace-pre-wrap" style={{ color: '#0A0A10' }}>
                  {JSON.stringify(item, null, 2)}
                </pre>
              </div>
            ))}
          </div>
        </div>
      );
    }
    
    if (type === "object") {
      const keys = Object.keys(value);
      return (
        <div className="flex flex-col h-full space-y-2">
          <div className="text-sm font-medium" style={{ color: '#0A0A10' }}>Object ({keys.length} keys)</div>
          <div className="flex-1 space-y-2 overflow-y-auto min-h-0">
            {keys.map((key) => (
              <div key={key} className="p-3 rounded-lg border" style={{ borderColor: '#D7B472', backgroundColor: '#F8F7F4' }}>
                <div className="text-xs font-mono mb-1" style={{ color: '#0A0A10', opacity: 0.7 }}>{key}</div>
                <pre className="text-sm whitespace-pre-wrap" style={{ color: '#0A0A10' }}>
                  {JSON.stringify(value[key], null, 2)}
                </pre>
              </div>
            ))}
          </div>
        </div>
      );
    }
    
    return (
      <div className="flex flex-col h-full space-y-2">
        <div className="text-sm font-medium" style={{ color: '#0A0A10' }}>String Value</div>
        <pre className="flex-1 text-sm whitespace-pre-wrap p-4 rounded-lg border overflow-y-auto min-h-0" style={{ borderColor: '#D7B472', backgroundColor: '#F8F7F4', color: '#0A0A10' }}>
          {String(value)}
        </pre>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold" style={{ color: '#0A0A10' }}>
          {title} ({filteredData.length} entries)
        </h3>
      </div>

      {filteredData.length === 0 ? (
        <div className="text-center py-12 rounded-lg border-2 border-dashed" style={{ borderColor: '#D7B472', color: '#0A0A10', opacity: 0.7 }}>
          <Database className="w-16 h-16 mx-auto mb-4" style={{ color: '#D7B472', opacity: 0.5 }} />
          <p className="text-lg mb-2">No data entries yet</p>
        </div>
      ) : (
        <div className="space-y-3">
          {/* Overview Cards */}
          {filteredData.map(([key, value]) => {
            const type = detectType(value);
            const preview = getOverviewPreview(value);

            return (
              <div
                key={key}
                onClick={() => handleViewDetails(key, value)}
                className="border rounded-lg p-4 cursor-pointer transition-colors"
                style={{ 
                  borderColor: '#D7B472', 
                  backgroundColor: '#FFFFFF'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#FF6B5A';
                  e.currentTarget.style.backgroundColor = '#F8F7F4';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#D7B472';
                  e.currentTarget.style.backgroundColor = '#FFFFFF';
                }}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h4 className="text-sm font-semibold font-mono" style={{ color: '#0A0A10' }}>
                        {key}
                      </h4>
                      <span className="text-xs px-2 py-0.5 rounded" style={{ backgroundColor: 'rgba(215, 180, 114, 0.15)', color: '#D7B472' }}>
                        {type}
                      </span>
                    </div>
                    <p className="text-sm line-clamp-2" style={{ color: '#0A0A10', opacity: 0.7 }}>
                      {preview}
                    </p>
                  </div>
                  <Eye className="w-5 h-5 flex-shrink-0 ml-3" style={{ color: '#FF6B5A' }} />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Drawer for Viewing/Editing Details */}
      <Drawer
        isOpen={isDrawerOpen}
        onClose={handleCancel}
        title={selectedKey ? `View/Edit: ${selectedKey}` : "Data Details"}
        description={selectedKey ? `View and edit the ${selectedKey} data entry` : ""}
        size="lg"
      >
        {selectedKey && (
          <div className="flex flex-col h-full">
            {/* View Mode */}
            {editingKey !== selectedKey ? (
              <div className="flex flex-col flex-1 min-h-0">
                <div className="flex items-center justify-between mb-4 flex-shrink-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs px-2 py-1 rounded" style={{ backgroundColor: 'rgba(215, 180, 114, 0.15)', color: '#D7B472' }}>
                      {detectType(data[selectedKey])}
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setEditingKey(selectedKey);
                        setEditForm({
                          key: selectedKey,
                          value: formatValue(data[selectedKey]),
                          type: detectType(data[selectedKey]),
                        });
                      }}
                      className="px-4 py-2 rounded-lg text-sm font-semibold transition-colors flex items-center gap-2"
                      style={{ backgroundColor: 'rgba(215, 180, 114, 0.15)', color: '#D7B472' }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(215, 180, 114, 0.25)'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgba(215, 180, 114, 0.15)'}
                    >
                      <Edit2 className="w-4 h-4" />
                      Edit
                    </button>
                    <button
                      onClick={() => handleRemove(selectedKey)}
                      className="px-4 py-2 rounded-lg text-sm font-semibold text-white transition-colors flex items-center gap-2"
                      style={{ backgroundColor: '#FF6B5A' }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#ff5540'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#FF6B5A'}
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete
                    </button>
                  </div>
                </div>
                <div className="flex-1 min-h-0">
                  {renderFullValue(data[selectedKey], selectedKey)}
                </div>
              </div>
            ) : (
              /* Edit Mode */
              <div className="flex flex-col flex-1 min-h-0">
                <div className="flex-1 flex flex-col min-h-0 mb-4">
                  <label className="block text-sm font-medium mb-2 flex-shrink-0" style={{ color: '#0A0A10' }}>
                    Value *
                  </label>
                  <textarea
                    value={editForm.value}
                    onChange={(e) =>
                      setEditForm({ ...editForm, value: e.target.value })
                    }
                    className="flex-1 w-full px-4 py-2 rounded-lg border text-sm focus:outline-none focus:ring-2 font-mono resize-none min-h-0"
                    style={{ borderColor: '#D7B472', color: '#0A0A10', backgroundColor: '#F8F7F4' }}
                    placeholder={editForm.type === "array" ? '["item1", "item2"]' : editForm.type === "object" ? '{"key": "value"}' : "Enter value"}
                  />
                </div>
                <div className="flex gap-3 pt-2 flex-shrink-0">
                  <button
                    onClick={handleSave}
                    className="flex-1 px-4 py-2 rounded-lg text-sm font-semibold text-white transition-colors flex items-center justify-center gap-2"
                    style={{ backgroundColor: '#D7B472' }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#c5a060'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#D7B472'}
                  >
                    <Save className="w-4 h-4" />
                    Save
                  </button>
                  <button
                    onClick={handleCancel}
                    className="flex-1 px-4 py-2 rounded-lg text-sm font-semibold transition-colors flex items-center justify-center gap-2"
                    style={{ backgroundColor: 'rgba(215, 180, 114, 0.15)', color: '#D7B472' }}
                  >
                    <X className="w-4 h-4" />
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </Drawer>
    </div>
  );
}

